import { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook to sync state with URL query parameters
 * @param {Object} options
 * @param {string} options.paramName - The query parameter name
 * @param {Function} options.fromQuery - Function to parse the query value (optional, defaults to JSON.parse)
 * @param {Function} options.toQuery - Function to serialize the value (optional, defaults to JSON.stringify)
 * @param {*} options.defaultValue - Default value when param is not in URL
 * @returns {[*, Function]} - State and setter tuple
 */
export function useQueryState({
  paramName,
  fromQuery,
  toQuery,
  defaultValue = null,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isUpdatingRef = useRef(false);
  const stateRef = useRef(null);

  const fromQueryFn = useCallback(
    (value) => {
      if (typeof fromQuery === 'function') {
        return fromQuery(value);
      }
      try {
        return value ? JSON.parse(value) : defaultValue;
      } catch {
        return defaultValue;
      }
    },
    [fromQuery, defaultValue]
  );

  const toQueryFn = useCallback(
    (value) => {
      if (typeof toQuery === 'function') {
        return toQuery(value);
      }
      return JSON.stringify(value);
    },
    [toQuery]
  );

  // Get initial value from URL or default
  const getInitialValue = () => {
    const urlValue = searchParams.get(paramName);
    const initialValue = fromQueryFn(urlValue);
    stateRef.current = initialValue;
    return initialValue;
  };

  const [state, setStateInternal] = useState(getInitialValue);

  // Keep ref in sync
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Custom setter that also updates URL - stable reference
  const setState = useCallback((newValue) => {
    const value = typeof newValue === 'function' ? newValue(stateRef.current) : newValue;
    stateRef.current = value;
    setStateInternal(value);

    // Update URL
    isUpdatingRef.current = true;
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      const isEmpty = value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0);

      if (isEmpty) {
        newParams.delete(paramName);
      } else {
        newParams.set(paramName, toQueryFn(value));
      }

      return newParams;
    }, { replace: true });

    // Reset flag after a tick
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  }, [paramName, toQueryFn, setSearchParams]);

  // Sync state when URL changes externally (e.g., browser back/forward)
  const searchParamsString = searchParams.toString();
  useEffect(() => {
    if (isUpdatingRef.current) return;

    const urlValue = searchParams.get(paramName);
    const parsedValue = fromQueryFn(urlValue);

    // Compare serialized values to avoid unnecessary updates
    const currentSerialized = toQueryFn(stateRef.current);
    const newSerialized = toQueryFn(parsedValue);

    if (currentSerialized !== newSerialized) {
      stateRef.current = parsedValue;
      setStateInternal(parsedValue);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsString, paramName]);

  return [state, setState];
}
